/**
 * CustomizationPage
 *
 * Entry point in the hamburger menu — visible ONLY to supervisors
 * whose state is marked strict_isolated. It's the per-state
 * administration hub for a restricted tenant.
 *
 * Sections:
 *   (a) Onboarding Process     — COMING IN ROUND 2
 *   (b) Key Metrics            — skipped per user (Round 2 ask)
 *   (c) Key Trackers           — skipped per user (Round 2 ask)
 *   (d) Monthly Goals          — skipped per user (Round 2 ask)
 *   (e) User Communications    — COMING IN ROUND 2
 *   (f) Calling                — COMING IN ROUND 2
 *   (g) Logo                   — LIVE (upload; applies to signup/home)
 *   (h) Theme                  — LIVE (ORB Green / ORB Black / ORB White)
 *
 * Every live section persists to state_branding via
 * PUT /api/admin/state-branding/{code} (supervisor-allowed for their
 * own state only).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStateScope } from '../context/StateScopeContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Sparkles, Image as ImageIcon, Palette, Upload, Check,
  ClipboardList, Phone, MessageSquare, BarChart2, Target, Trophy, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const SectionHeader = ({ icon: Icon, title, note, testid }) => (
  <div className="flex items-start gap-2 mb-3" data-testid={testid}>
    <Icon className="w-4 h-4 text-[#586D52] mt-1" />
    <div>
      <p className="text-sm font-bold uppercase tracking-wider">{title}</p>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
    </div>
  </div>
);

const ClickableSection = ({ icon, title, subtitle, onClick, testid }) => (
  <Card
    className="rounded-2xl cursor-pointer hover:border-[#586D52] transition-colors"
    onClick={onClick}
    data-testid={testid}
  >
    <CardContent className="pt-4 pb-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#586D52]/10 flex items-center justify-center">
        {React.createElement(icon, { className: 'w-4 h-4 text-[#586D52]' })}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </CardContent>
  </Card>
);

const ComingSoonSection = ({ icon, title }) => (
  <Card className="rounded-2xl border-dashed opacity-80">
    <CardContent className="pt-4 pb-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#586D52]/10 flex items-center justify-center">
        {React.createElement(icon, { className: 'w-4 h-4 text-[#586D52]' })}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-[11px] text-muted-foreground">Coming soon</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </CardContent>
  </Card>
);

const THEMES = [
  { id: 'orb_green', label: 'ORB Green', hint: 'Light mode', swatch: '#586D52' },
  { id: 'orb_black', label: 'ORB Black', hint: 'Dark mode',  swatch: '#1a1a1a' },
  { id: 'orb_white', label: 'ORB White', hint: 'Soft neutral', swatch: '#FAFAF7' },
];

const ThemeSection = ({ branding, save, saving }) => {
  const { setTheme } = useTheme();
  const current = branding?.theme || 'orb_green';
  // Track only the admin's pending (unsaved) pick. When null, we
  // derive the selected card directly from the saved branding so a
  // hard refresh always paints the correct Check icon on first frame.
  const [pendingPick, setPendingPick] = useState(null);
  const pick = pendingPick ?? current;

  // Reset pending pick whenever the saved theme changes (e.g. after
  // save success OR after /admin/state-branding refetch).
  useEffect(() => { setPendingPick(null); }, [current]);

  const apply = async () => {
    await save({ theme: pick });
    if (pick === 'orb_black') setTheme('dark');
    else setTheme('light');
    setPendingPick(null);
    toast.success('Theme applied');
  };
  return (
    <Card className="rounded-2xl" data-testid="customization-theme">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#586D52]" /> Theme
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {THEMES.map((t) => {
            const active = pick === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setPendingPick(t.id)}
                className={`rounded-xl border p-3 text-left transition-colors ${
                  active ? 'border-[#586D52] bg-[#586D52]/10' : 'border-gray-200 dark:border-[#2a2a2a] hover:border-[#586D52]'
                }`}
                data-testid={`theme-pick-${t.id}`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="w-5 h-5 rounded-full border border-black/10"
                    style={{ background: t.swatch }}
                  />
                  <p className="text-sm font-semibold flex-1">{t.label}</p>
                  {active && <Check className="w-4 h-4 text-[#586D52]" />}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{t.hint}</p>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">
          ORB White uses a soft neutral palette (#FAFAF7 / #E6E6E0 / #809A75). Applied to the login page and entire app for every user in this restricted state.
        </p>
        <Button
          onClick={apply}
          disabled={saving || pick === current}
          className="bg-[#586D52] hover:bg-[#4a5d46]"
          data-testid="theme-save-btn"
        >
          {saving ? 'Saving…' : 'Apply Theme'}
        </Button>
      </CardContent>
    </Card>
  );
};

const LogoSection = ({ branding, save, saving }) => {
  const [preview, setPreview] = useState(branding?.logo_url || null);
  useEffect(() => { setPreview(branding?.logo_url || null); }, [branding?.logo_url]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be smaller than 2 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const doSave = async () => {
    await save({ logo_url: preview });
    toast.success('Logo saved');
  };
  const doClear = async () => {
    setPreview(null);
    await save({ logo_url: '' });
    toast.success('Logo cleared');
  };

  return (
    <Card className="rounded-2xl" data-testid="customization-logo">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[#586D52]" /> Logo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 rounded-xl bg-white dark:bg-[#0d0d0d] border border-gray-200 dark:border-[#2a2a2a] flex items-center justify-center overflow-hidden">
            {preview ? (
              <img src={preview} alt="Logo preview" className="w-full h-full object-contain" />
            ) : (
              <ImageIcon className="w-6 h-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 text-xs text-muted-foreground">
            Replaces the ORB logo on the sign-in/sign-up page and home header for every user in your state.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Label
            htmlFor="customization-logo-file"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#586D52] text-white cursor-pointer hover:bg-[#4a5d46]"
            data-testid="logo-upload-label"
          >
            <Upload className="w-3.5 h-3.5" /> Choose file
          </Label>
          <input
            id="customization-logo-file"
            type="file"
            accept="image/*"
            onChange={onPick}
            className="hidden"
            data-testid="logo-file-input"
          />
          <Button
            size="sm"
            onClick={doSave}
            disabled={saving || preview === branding?.logo_url}
            className="bg-[#586D52] hover:bg-[#4a5d46]"
            data-testid="logo-save-btn"
          >
            {saving ? 'Saving…' : 'Save Logo'}
          </Button>
          {branding?.logo_url && (
            <Button
              size="sm"
              variant="outline"
              onClick={doClear}
              disabled={saving}
              data-testid="logo-clear-btn"
            >
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function CustomizationPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { isRestricted, stateCode, displayName, reload } = useStateScope();
  const [branding, setBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAllowed = isRestricted && user?.role === 'supervisor';

  useEffect(() => {
    if (!isAllowed || !stateCode) { setLoadingBranding(false); return; }
    (async () => {
      try {
        const r = await api.get(`/admin/state-branding/${encodeURIComponent(stateCode)}`);
        setBranding(r.data || {});
      } catch (e) {
        toast.error('Could not load your state branding');
      } finally {
        setLoadingBranding(false);
      }
    })();
  }, [api, isAllowed, stateCode]);

  const save = async (patch) => {
    if (!stateCode) return;
    setSaving(true);
    try {
      const r = await api.put(
        `/admin/state-branding/${encodeURIComponent(stateCode)}`,
        patch,
      );
      setBranding(r.data?.branding || {});
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!isAllowed) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold">Restricted-state supervisors only</p>
        <p className="text-xs text-muted-foreground mt-1">
          This tab is available to supervisors inside a Restricted State.
        </p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/')}>Back home</Button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24"
      data-testid="customization-page"
    >
      <div className="max-w-3xl mx-auto p-3">
        <div className="flex items-center gap-2 mb-3">
          <Button size="icon" variant="ghost" onClick={() => navigate(-1)} data-testid="customization-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#586D52]" />
            Customization
          </h1>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Per-state branding and admin tools for <strong>{displayName || stateCode}</strong>. Changes here apply to every user in your state.
        </p>

        {loadingBranding ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-3">
            <SectionHeader icon={Palette} title="Appearance" note="Live — applies to every user in your state" testid="sec-appearance" />
            <LogoSection branding={branding} save={save} saving={saving} />
            <ThemeSection branding={branding} save={save} saving={saving} />

            <SectionHeader icon={ClipboardList} title="Processes & Tools" note="Live now" testid="sec-processes" />
            <ClickableSection
              icon={ClipboardList}
              title="Onboarding Process"
              subtitle="Toggle steps, upload your own intro videos, add interview questions, attach NDA"
              onClick={() => navigate('/customization/onboarding')}
              testid="customization-onboarding-link"
            />
            <ClickableSection
              icon={MessageSquare}
              title="User Communications"
              subtitle={`State-scoped — only ${displayName || stateCode} users`}
              onClick={() => navigate('/admin/user-communications')}
              testid="customization-comms-link"
            />
            <ClickableSection
              icon={Phone}
              title="Calling"
              subtitle={`State-scoped — only ${displayName || stateCode} users`}
              onClick={() => navigate('/calls')}
              testid="customization-calling-link"
            />

            <SectionHeader icon={BarChart2} title="Performance" note="Pending product definition" testid="sec-performance" />
            <ComingSoonSection icon={BarChart2} title="Key Metrics" />
            <ComingSoonSection icon={Target} title="Key Trackers" />
            <ComingSoonSection icon={Trophy} title="Monthly Goals" />
          </div>
        )}
      </div>
    </div>
  );
}
